import React from "react";
import CampaignImagePicker from "./campaignImagePicker";

interface RetailCampaignFormProps {}

const baseUrl = process.env.REACT_APP_API_URL || "";

const RetailCampaignForm: React.FC<RetailCampaignFormProps> = () => {
  const [subject, setSubject] = React.useState("");
  const [preheader, setPreheader] = React.useState("");
  const [intro, setIntro] = React.useState("");
  const [educationBody, setEducationBody] = React.useState("");
  const [ctaText, setCtaText] = React.useState(
    "Ask your local retailer about Ridgetop Botanicals products."
  );
  const [footerNotes, setFooterNotes] = React.useState(
    "21+ only. Please consume responsibly. Local laws apply. This email is for educational and informational purposes only and is not medical advice."
  );

  // Hero image state
  const [heroImageUrl, setHeroImageUrl] = React.useState("");
  const [heroUploading, setHeroUploading] = React.useState(false);
  const [heroUploadError, setHeroUploadError] = React.useState<string | null>(
    null
  );
  const [isHeroPickerOpen, setIsHeroPickerOpen] = React.useState(false);

  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  function resolveImageUrl(url: string): string {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${baseUrl}${url.startsWith("/") ? url : `/${url}`}`;
  }

  async function handleHeroFileChange(
    e: React.ChangeEvent<HTMLInputElement>
  ) {
    const file = e.target.files?.[0];
    if (!file) return;

    setHeroUploadError(null);
    setHeroUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("kind", "hero");

      const res = await fetch(`${baseUrl}/api/campaigns/images/upload`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("Hero image upload error:", res.status, text);
        throw new Error(`Upload failed: ${res.status}`);
      }

      const json = await res.json();
      const image = json.image || json;
      const storedUrl = image.url || "";
      const publicUrl = image.publicUrl || "";
      const finalUrl = publicUrl || storedUrl;

      if (!finalUrl) {
        throw new Error("Upload succeeded but no URL returned.");
      }

      setHeroImageUrl(finalUrl);
    } catch (err: any) {
      console.error("Failed to upload hero image:", err);
      setHeroUploadError(err?.message || "Hero image upload failed.");
    } finally {
      setHeroUploading(false);
      e.target.value = "";
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!subject.trim()) {
      setError("Subject is required.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(
        `${baseUrl}/api/campaigns/retail-education`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            // listIds omitted -> backend defaults to [5] (Retail Fans 21+)
            subject: subject.trim(),
            preheader: preheader.trim(),
            intro: intro.trim(),
            educationBody: educationBody.trim(),
            ctaText: ctaText.trim(),
            footerNotes: footerNotes.trim(),
            heroImageUrl: heroImageUrl || null,
          }),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        console.error("Retail campaign error:", res.status, text);
        alert("Failed to save retail campaign draft (check backend logs).");
        throw new Error(`Failed to create campaign: ${res.status}`);
      }

      const json = await res.json();
      const msg = `Retail education draft created in Listmonk (ID ${json.listmonkCampaignId}, status: ${json.status}).`;
      setSuccess(msg);
      alert("Retail education draft successfully sent to backend.");
    } catch (err: any) {
      console.error("Retail campaign request failed:", err);
      setError(err?.message || "Failed to create retail campaign.");
      alert("Error saving retail campaign draft (check console).");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {/* Hero image picker modal */}
      <CampaignImagePicker
        isOpen={isHeroPickerOpen}
        onClose={() => setIsHeroPickerOpen(false)}
        onSelect={(url: string) => setHeroImageUrl(url)}
        title="Choose a hero image for this Retail Education / Farm Update email"
      />

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4"
      >
        {/* Left: form fields */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold">
            Retail Education / Farm Update Details
          </h4>

          {/* Legal guidance box */}
          <div className="text-xs border border-amber-300 bg-amber-50 rounded-md p-3 space-y-1">
            <p className="font-semibold text-amber-800">
              Reminder: Retail emails are education-only.
            </p>
            <ul className="list-disc list-inside text-amber-900 space-y-0.5">
              <li>No pricing or discounts.</li>
              <li>No potency numbers (THC%, total cannabinoids%, etc.).</li>
              <li>No medical claims or promises about effects.</li>
              <li>No direct sales or delivery from the farm.</li>
              <li>
                It’s safe to talk about the farm, process, testing, stories,
                and general aroma/flavor.
              </li>
            </ul>
            <p className="mt-1">
              For anything borderline, check Vermont CCB rules or your attorney.
            </p>
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          {success && (
            <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
              {success}
            </div>
          )}

          {/* Hero image controls */}
          <div className="border border-gray-200 rounded-md p-3 bg-gray-50 space-y-2">
            <p className="text-xs font-semibold text-gray-700">
              Optional hero image
            </p>
            <p className="text-[11px] text-gray-500 mb-1">
              Use farm, field, or process photos — not consumption scenes or
              product glam shots.
            </p>

            <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Upload new hero image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  className="text-xs"
                  onChange={handleHeroFileChange}
                  disabled={heroUploading}
                />
                {heroUploading && (
                  <p className="text-[11px] text-gray-500 mt-1">
                    Uploading hero image…
                  </p>
                )}
                {heroUploadError && (
                  <p className="text-[11px] text-red-600 mt-1">
                    {heroUploadError}
                  </p>
                )}
              </div>

              <div className="sm:ml-4">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Or choose existing
                </label>
                <button
                  type="button"
                  className="px-2 py-1 text-xs rounded-md border border-gray-300 bg-white hover:bg-gray-100"
                  onClick={() => setIsHeroPickerOpen(true)}
                >
                  Pick from hero library
                </button>
              </div>
            </div>

            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Hero image URL (optional, read-only)
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-[11px] bg-gray-100"
                value={heroImageUrl}
                readOnly
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Subject</label>
            <input
              type="text"
              className="w-full border rounded px-2 py-1 text-sm"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Field notes from the hilltop"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Preheader</label>
            <input
              type="text"
              className="w-full border rounded px-2 py-1 text-sm"
              value={preheader}
              onChange={(e) => setPreheader(e.target.value)}
              placeholder="What’s growing, what we’re learning, and how we test our flower."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Intro</label>
            <textarea
              className="w-full border rounded px-2 py-1 text-sm h-24"
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              placeholder="Quick farm update, weather, harvest notes, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Education / story body
            </label>
            <textarea
              className="w-full border rounded px-2 py-1 text-sm h-32"
              value={educationBody}
              onChange={(e) => setEducationBody(e.target.value)}
              placeholder="Explain something useful (e.g. what a COA is, how you grow, sustainability, etc.)."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">CTA text</label>
            <textarea
              className="w-full border rounded px-2 py-1 text-sm h-20"
              value={ctaText}
              onChange={(e) => setCtaText(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Footer notes / disclaimer
            </label>
            <textarea
              className="w-full border rounded px-2 py-1 text-xs h-20"
              value={footerNotes}
              onChange={(e) => setFooterNotes(e.target.value)}
            />
          </div>

          <div className="pt-2">
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm disabled:opacity-60"
              disabled={submitting}
            >
              {submitting
                ? "Creating draft…"
                : "Save Retail Draft in Listmonk"}
            </button>
          </div>
        </div>

        {/* Right: preview */}
        <div className="space-y-3 border rounded-lg p-3 bg-gray-50">
          <h4 className="text-lg font-semibold">Email Preview (simplified)</h4>

          <div className="text-sm">
            <div>
              <span className="font-semibold">Subject:</span>{" "}
              {subject || "(no subject yet)"}
            </div>
            <div className="text-gray-500">
              <span className="font-semibold">Preheader:</span>{" "}
              {preheader || "(no preheader yet)"}
            </div>
          </div>

          <hr />

          {heroImageUrl && (
            <div className="mb-2">
              <p className="text-xs text-gray-500 mb-1">Hero image preview</p>
              <img
                src={resolveImageUrl(heroImageUrl)}
                alt="Hero preview"
                className="w-full max-h-40 object-cover rounded border border-gray-200"
              />
            </div>
          )}

          <div className="text-sm space-y-2">
            <p className="text-xs text-gray-500">
              (Greeting will be inserted by the Ridgetop base email template.)
            </p>

            <p>{intro || "(intro will go here…)"}</p>

            <h5 className="mt-2 font-semibold">On the hilltop this week</h5>
            <p>{educationBody || "(education / story content will go here…)"}</p>

            <p className="mt-4 text-gray-500 text-xs">
              Retail emails are for education and brand storytelling only. No
              pricing, potency numbers, or medical claims will be included in
              the final send.
            </p>

            <p className="mt-4">{ctaText}</p>

            <p className="mt-4 text-[11px] text-gray-500">{footerNotes}</p>
          </div>
        </div>
      </form>
    </>
  );
};

export default RetailCampaignForm;
